import React, { useMemo } from "react";
import { Text } from "@react-three/drei";
import { CAMPAIGN_PHASES } from "../game/campaign";
import { getPerformanceStats } from "../game/runStats";
import type { BucketStat, RunPerformanceStats } from "../game/runStats";
import {
  useGameStore,
  Grade,
  ObstacleRunEvent,
  TargetRunEvent,
} from "../game/useGameStore";
import {
  colors,
  gradeColors,
  gradeEnglish,
  gradeKanji,
  gradeShort,
  primaryButton,
  secondaryButton,
  typography,
} from "./theme";
import { SURVIVAL_TRACKS } from "../game/survival";
import { xrStore } from "../app/xrStore";

function formatSurvivalDuration(seconds: number): string {
  const wholeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(wholeSeconds / 60);
  return `${minutes}:${(wholeSeconds % 60).toString().padStart(2, "0")}`;
}

const RESULT_TEXT_PROPS = {
  renderOrder: 2001,
  "material-depthTest": true,
} as const;

const RESULT_GRADE_ORDER: Grade[] = [
  "MISS",
  "LATE",
  "GOOD",
  "GREAT",
  "PERFECT",
  "IPPON",
];

function useRunStats(): RunPerformanceStats {
  const targetEvents = useGameStore((s) => s.runTargetEvents);
  const obstacleEvents = useGameStore((s) => s.runObstacleEvents);
  return useMemo(
    () => getPerformanceStats(targetEvents, obstacleEvents),
    [targetEvents, obstacleEvents],
  );
}

function currentRunTitle(): string {
  const s = useGameStore.getState();
  if (s.mode === "survival") return "Survival Endless";
  const phase = CAMPAIGN_PHASES[s.lastRunPhaseIndex] ?? CAMPAIGN_PHASES[0];
  return `${phase.numeral} - ${phase.name}`;
}

function GradeBreakdownRow({
  counts,
  y,
}: {
  counts: Record<Grade, number>;
  y: number;
}) {
  const gap = 0.205;
  const startX = -(gap * (RESULT_GRADE_ORDER.length - 1)) / 2;
  return (
    <group position={[0, y, 0]}>
      {RESULT_GRADE_ORDER.map((grade, i) => (
        <group key={grade} position={[startX + i * gap, 0, 0]}>
          <Text
            {...RESULT_TEXT_PROPS}
            fontSize={0.032}
            color={gradeColors[grade]}
            anchorX="center"
            anchorY="middle"
            position={[0, 0.06, 0]}
          >
            {gradeKanji[grade]}
          </Text>
          <Text
            {...RESULT_TEXT_PROPS}
            fontSize={0.04}
            color={gradeColors[grade]}
            anchorX="center"
            anchorY="middle"
            position={[0, 0, 0]}
          >
            {gradeShort[grade]}
          </Text>
          <Text
            {...RESULT_TEXT_PROPS}
            fontSize={0.05}
            color={gradeColors[grade]}
            anchorX="center"
            anchorY="middle"
            position={[0, -0.075, 0]}
          >
            {counts[grade]}
          </Text>
        </group>
      ))}
    </group>
  );
}

function PanelBacking({ height = 1.76 }: { height?: number }) {
  return (
    <mesh position={[0, -0.1, -0.025]} renderOrder={2000}>
      <planeGeometry args={[1.62, height]} />
      <meshBasicMaterial
        color={colors.ink}
        transparent
        opacity={0.98}
        depthTest
        depthWrite={false}
      />
    </mesh>
  );
}

function Separator({ y }: { y: number }) {
  return (
    <mesh position={[0, y, -0.01]} renderOrder={2001}>
      <planeGeometry args={[1.26, 0.006]} />
      <meshBasicMaterial
        color={colors.border}
        transparent
        opacity={0.72}
        depthTest
        depthWrite={false}
      />
    </mesh>
  );
}

function StatText({
  label,
  value,
  x,
  y,
  color = colors.washi,
}: {
  label: string;
  value: string;
  x: number;
  y: number;
  color?: string;
}) {
  return (
    <group position={[x, y, 0]}>
      <Text
        {...RESULT_TEXT_PROPS}
        fontSize={0.038}
        color={colors.border}
        anchorX="center"
        anchorY="middle"
        position={[0, 0.052, 0]}
      >
        {label}
      </Text>
      <Text
        {...RESULT_TEXT_PROPS}
        fontSize={0.066}
        color={color}
        anchorX="center"
        anchorY="middle"
        position={[0, -0.022, 0]}
      >
        {value}
      </Text>
    </group>
  );
}

function BucketRows({
  title,
  rows,
  x,
  y,
}: {
  title: string;
  rows: BucketStat[];
  x: number;
  y: number;
}) {
  return (
    <group position={[x, y, 0]}>
      <Text
        {...RESULT_TEXT_PROPS}
        fontSize={0.046}
        color={colors.amber}
        anchorX="left"
        anchorY="middle"
        position={[0, 0, 0]}
      >
        {title}
      </Text>
      {rows.slice(0, 5).map((row, index) => (
        <Text
          key={`${title}-${row.label}`}
          {...RESULT_TEXT_PROPS}
          fontSize={0.037}
          color={colors.washi}
          anchorX="left"
          anchorY="middle"
          position={[0, -0.08 - index * 0.072, 0]}
        >
          {`${row.label.toUpperCase()} ${Math.round(row.hitRate * 100)}% ${row.hits}/${row.total}`}
        </Text>
      ))}
    </group>
  );
}

function ResultDetails3D({
  stats,
  score,
  peakCombo,
}: {
  stats: RunPerformanceStats;
  score: number;
  peakCombo: number;
}) {
  const best = stats.bestCategory;
  const weakest = stats.weakestCategory;

  return (
    <group position={[0, 1.66, -2.45]}>
      <PanelBacking height={2.42} />
      <Text
        {...RESULT_TEXT_PROPS}
        fontSize={0.072}
        color={colors.amber}
        anchorX="center"
        anchorY="middle"
        position={[0, 0.88, 0]}
      >
        PERFORMANCE
      </Text>
      <Separator y={0.76} />
      <StatText
        label="SCORE"
        value={score.toString().padStart(6, "0")}
        x={-0.42}
        y={0.62}
      />
      <StatText
        label="HIT RATE"
        value={stats.accuracyLabel}
        x={0}
        y={0.62}
        color={colors.gold}
      />
      <StatText label="COMBO" value={`x${peakCombo}`} x={0.42} y={0.62} />
      <StatText
        label="TARGETS"
        value={`${stats.hitTargets}/${stats.totalTargets}`}
        x={-0.42}
        y={0.42}
      />
      <StatText
        label="MISSES"
        value={`${stats.missedTargets + stats.wrongDirectionTargets}`}
        x={0}
        y={0.42}
      />
      <StatText
        label="OBSTACLES"
        value={`${stats.obstacleCleared}/${stats.obstacleTotal}`}
        x={0.42}
        y={0.42}
      />
      <Separator y={0.25} />
      <GradeBreakdownRow counts={stats.byGrade} y={0.11} />
      <Separator y={-0.06} />
      <BucketRows
        title="DIRECTION"
        rows={stats.byDirection}
        x={-0.62}
        y={-0.17}
      />
      <BucketRows title="HAND" rows={stats.byHand} x={0.08} y={-0.17} />
      <BucketRows title="SPEED" rows={stats.bySpeed} x={-0.62} y={-0.62} />
      <BucketRows title="SIZE" rows={stats.bySize} x={0.08} y={-0.62} />
      <Text
        {...RESULT_TEXT_PROPS}
        fontSize={0.04}
        color={colors.gold}
        anchorX="center"
        anchorY="middle"
        position={[0, -1.02, 0]}
      >
        {best
          ? `BEST ${best.label.toUpperCase()} ${Math.round(best.hitRate * 100)}%`
          : "BEST CATEGORY N/A"}
      </Text>
      <Text
        {...RESULT_TEXT_PROPS}
        fontSize={0.04}
        color={colors.amber}
        anchorX="center"
        anchorY="middle"
        position={[0, -1.11, 0]}
      >
        {weakest
          ? `WEAKEST ${weakest.label.toUpperCase()} ${Math.round(weakest.hitRate * 100)}%`
          : "WEAKEST CATEGORY N/A"}
      </Text>
      <Text
        {...RESULT_TEXT_PROPS}
        fontSize={0.038}
        color={colors.border}
        anchorX="center"
        anchorY="middle"
        position={[0, -1.23, 0]}
      >
        B/Y or A/X Back - Right Stick Summary
      </Text>
    </group>
  );
}

function LeaderboardRow({
  rank,
  score,
  date,
  highlighted,
  y,
}: {
  rank: number;
  score: number;
  date: string;
  highlighted: boolean;
  y: number;
}) {
  const color = highlighted ? colors.amber : colors.washi;
  const dateLabel = new Date(date).toLocaleDateString(undefined, {
    month: "2-digit",
    day: "2-digit",
  });
  return (
    <group position={[0, y, 0]}>
      <Text
        {...RESULT_TEXT_PROPS}
        fontSize={0.075}
        color={color}
        anchorX="left"
        anchorY="middle"
        position={[-0.55, 0, 0]}
      >
        {`#${rank}`}
      </Text>
      <Text
        {...RESULT_TEXT_PROPS}
        fontSize={0.09}
        color={color}
        anchorX="left"
        anchorY="middle"
        position={[-0.4, 0, 0]}
      >
        {score.toLocaleString("en-US")}
      </Text>
      {highlighted && (
        <Text
          {...RESULT_TEXT_PROPS}
          fontSize={0.055}
          color={colors.amber}
          anchorX="left"
          anchorY="middle"
          position={[0.16, 0, 0]}
        >
          THIS RUN
        </Text>
      )}
      <Text
        {...RESULT_TEXT_PROPS}
        fontSize={0.06}
        color={color}
        anchorX="right"
        anchorY="middle"
        position={[0.55, 0, 0]}
      >
        {dateLabel}
      </Text>
    </group>
  );
}

export function ResultScreen3D() {
  const phase = useGameStore((s) => s.phase);
  const mode = useGameStore((s) => s.mode);
  const score = useGameStore((s) => s.score);
  const lastRunPassed = useGameStore((s) => s.lastRunPassed);
  const lastRunPhaseIndex = useGameStore((s) => s.lastRunPhaseIndex);
  const lastRunThreshold = useGameStore((s) => s.lastRunThreshold);
  const phaseBest = useGameStore((s) => s.phaseBest);
  const survivalBest = useGameStore((s) => s.survivalBest);
  const survivalRuns = useGameStore((s) => s.survivalRuns);
  const lastSurvivalWaves = useGameStore((s) => s.lastSurvivalWaves);
  const lastSurvivalDuration = useGameStore((s) => s.lastSurvivalDuration);
  const lastSurvivalTrackIndex = useGameStore((s) => s.lastSurvivalTrackIndex);
  const lastSurvivalWasRecord = useGameStore((s) => s.lastSurvivalWasRecord);
  const lastSurvivalRunDate = useGameStore((s) => s.lastSurvivalRunDate);
  const peakCombo = useGameStore((s) => s.peakCombo);
  const gradeCounts = useGameStore((s) => s.gradeCounts);
  const runKataLanded = useGameStore((s) => s.runKataLanded);
  const runKataTotal = useGameStore((s) => s.runKataTotal);
  const runKataBonus = useGameStore((s) => s.runKataBonus);
  const resultDetailsOpen = useGameStore((s) => s.resultDetailsOpen);
  const stats = useRunStats();

  if (phase !== "win" && phase !== "fail") return null;
  if (resultDetailsOpen) {
    return (
      <ResultDetails3D stats={stats} score={score} peakCombo={peakCombo} />
    );
  }

  if (mode === "survival") {
    const isNewBest = lastSurvivalWasRecord;
    const topRuns = survivalRuns.slice(0, 3);

    return (
      <group position={[0, 1.65, -2.45]}>
        <PanelBacking height={1.94} />
        <Text
          {...RESULT_TEXT_PROPS}
          fontSize={0.095}
          color={colors.amber}
          anchorX="center"
          anchorY="middle"
          position={[0, 0.73, 0]}
        >
          SURVIVAL - GAME OVER
        </Text>
        <Separator y={0.59} />
        <Text
          {...RESULT_TEXT_PROPS}
          fontSize={0.065}
          color={colors.washi}
          anchorX="center"
          anchorY="middle"
          position={[0, 0.47, 0]}
        >
          RUN SCORE
        </Text>
        <Text
          {...RESULT_TEXT_PROPS}
          fontSize={0.21}
          color={colors.steel}
          anchorX="center"
          anchorY="middle"
          position={[0, 0.29, 0]}
        >
          {score.toString().padStart(6, "0")}
        </Text>
        <StatText
          label="HIT RATE"
          value={stats.accuracyLabel}
          x={-0.42}
          y={0.05}
          color={colors.gold}
        />
        <StatText
          label="TARGETS"
          value={`${stats.hitTargets}/${stats.totalTargets}`}
          x={0}
          y={0.05}
        />
        <StatText label="COMBO" value={`x${peakCombo}`} x={0.42} y={0.05} />
        <StatText
          label="TIME"
          value={formatSurvivalDuration(lastSurvivalDuration)}
          x={-0.28}
          y={-0.15}
        />
        <StatText
          label="TRACK"
          value={`${lastSurvivalTrackIndex + 1}/${SURVIVAL_TRACKS.length}`}
          x={0.28}
          y={-0.15}
        />
        <Separator y={-0.31} />
        <Text
          {...RESULT_TEXT_PROPS}
          fontSize={0.058}
          color={colors.washi}
          anchorX="center"
          anchorY="middle"
          position={[0, -0.41, 0]}
        >
          PERSONAL BESTS
        </Text>
        {topRuns.map((run, i) => (
          <LeaderboardRow
            key={run.date}
            rank={i + 1}
            score={run.score}
            date={run.date}
            highlighted={run.date === lastSurvivalRunDate}
            y={-0.53 - i * 0.1}
          />
        ))}
        {isNewBest && (
          <Text
            {...RESULT_TEXT_PROPS}
            fontSize={0.058}
            color={colors.gold}
            anchorX="center"
            anchorY="middle"
            position={[0, -0.86, 0]}
          >
            NEW RECORD
          </Text>
        )}
        <Text
          {...RESULT_TEXT_PROPS}
          fontSize={0.038}
          color={colors.border}
          anchorX="center"
          anchorY="middle"
          position={[0, -1.0, 0]}
        >
          B/Y Retry - A/X Retry - Right Stick Details - Left Stick Menu
        </Text>
      </group>
    );
  }

  const campaignPhase =
    CAMPAIGN_PHASES[lastRunPhaseIndex] ?? CAMPAIGN_PHASES[0];
  const passed = lastRunPassed === true;
  const title = passed ? "PASSED" : "TRY AGAIN";
  const titleKanji = passed ? "PASS" : "RETRY";
  const titleColor = passed ? colors.gold : colors.amber;
  const fillPct = Math.min(1, score / Math.max(1, lastRunThreshold));
  const best = phaseBest[lastRunPhaseIndex] ?? 0;
  const isRecord = score > 0 && score === best;

  return (
    <group position={[0, 1.66, -2.45]}>
      <PanelBacking height={2.46} />
      <Text
        {...RESULT_TEXT_PROPS}
        fontSize={0.072}
        color={colors.steel}
        anchorX="center"
        anchorY="middle"
        position={[0, 0.88, 0]}
      >
        {`${campaignPhase.numeral} - ${campaignPhase.name}`}
      </Text>
      <Text
        {...RESULT_TEXT_PROPS}
        fontSize={0.13}
        color={titleColor}
        anchorX="center"
        anchorY="middle"
        position={[0, 0.7, 0]}
      >
        {titleKanji}
      </Text>
      <Text
        {...RESULT_TEXT_PROPS}
        fontSize={0.086}
        color={titleColor}
        anchorX="center"
        anchorY="middle"
        position={[0, 0.56, 0]}
      >
        {title}
      </Text>
      <Separator y={0.44} />
      <Text
        {...RESULT_TEXT_PROPS}
        fontSize={0.052}
        color={colors.washi}
        anchorX="center"
        anchorY="middle"
        position={[0, 0.34, 0]}
      >
        SCORE
      </Text>
      <Text
        {...RESULT_TEXT_PROPS}
        fontSize={0.17}
        color={colors.steel}
        anchorX="center"
        anchorY="middle"
        position={[0, 0.19, 0]}
      >
        {score.toString().padStart(6, "0")}
      </Text>
      <Text
        {...RESULT_TEXT_PROPS}
        fontSize={0.047}
        color={colors.washi}
        anchorX="center"
        anchorY="middle"
        position={[0, 0.05, 0]}
      >
        {`THRESHOLD ${lastRunThreshold.toLocaleString("en-US")}`}
      </Text>
      <group position={[0, -0.04, 0]}>
        <mesh position={[0, 0, -0.002]} renderOrder={2001}>
          <planeGeometry args={[1.25, 0.045]} />
          <meshBasicMaterial
            color={colors.wood}
            transparent
            opacity={0.85}
            depthTest
            depthWrite={false}
          />
        </mesh>
        <mesh
          position={[-0.625 + (1.25 * fillPct) / 2, 0, 0]}
          renderOrder={2002}
        >
          <planeGeometry args={[1.25 * fillPct, 0.045]} />
          <meshBasicMaterial
            color={passed ? colors.gold : colors.amber}
            transparent
            opacity={0.95}
            depthTest
            depthWrite={false}
          />
        </mesh>
      </group>
      <StatText
        label="HIT RATE"
        value={stats.accuracyLabel}
        x={-0.45}
        y={-0.21}
        color={colors.gold}
      />
      <StatText
        label="TARGETS"
        value={`${stats.hitTargets}/${stats.totalTargets}`}
        x={0}
        y={-0.21}
      />
      <StatText
        label="MISSES"
        value={`${stats.missedTargets + stats.wrongDirectionTargets}`}
        x={0.45}
        y={-0.21}
      />
      <StatText label="COMBO" value={`x${peakCombo}`} x={-0.3} y={-0.43} />
      <StatText
        label="TENOUCHI"
        value={`${stats.tenouchiHits}`}
        x={0.3}
        y={-0.43}
      />
      <Separator y={-0.57} />
      <GradeBreakdownRow counts={gradeCounts} y={-0.7} />
      <Separator y={-0.86} />
      {runKataTotal > 0 && (
        <>
          <Text
            {...RESULT_TEXT_PROPS}
            fontSize={0.06}
            color={colors.amber}
            anchorX="left"
            anchorY="middle"
            position={[-0.55, -0.96, 0]}
          >
            KATA COMPLETE
          </Text>
          <Text
            {...RESULT_TEXT_PROPS}
            fontSize={0.06}
            color={colors.steel}
            anchorX="right"
            anchorY="middle"
            position={[0.28, -0.96, 0]}
          >
            {`${runKataLanded}/${runKataTotal}`}
          </Text>
          <Text
            {...RESULT_TEXT_PROPS}
            fontSize={0.06}
            color={colors.amber}
            anchorX="right"
            anchorY="middle"
            position={[0.55, -0.96, 0]}
          >
            {`+${runKataBonus.toLocaleString("en-US")}`}
          </Text>
        </>
      )}
      <Text
        {...RESULT_TEXT_PROPS}
        fontSize={0.048}
        color={isRecord ? colors.gold : colors.washi}
        anchorX="center"
        anchorY="middle"
        position={[0, -1.09, 0]}
      >
        {isRecord
          ? `NEW RECORD - BEST ${best.toLocaleString("en-US")}`
          : `PHASE BEST ${best.toLocaleString("en-US")}`}
      </Text>
      <Text
        {...RESULT_TEXT_PROPS}
        fontSize={0.038}
        color={colors.border}
        anchorX="center"
        anchorY="middle"
        position={[0, -1.23, 0]}
      >
        B/Y Next - A/X Retry - Right Stick Details - Left Stick Menu
      </Text>
    </group>
  );
}

function EventLabel({ event }: { event: TargetRunEvent | ObstacleRunEvent }) {
  if ("dir" in event) {
    const reason =
      event.result === "wrong-direction" ? "wrong direction" : event.result;
    return (
      <>
        {event.t.toFixed(1)}s - {event.dir} - {reason}
      </>
    );
  }
  return (
    <>
      {event.t.toFixed(1)}s - {event.type} - obstacle hit
    </>
  );
}

function StatsTable({ title, rows }: { title: string; rows: BucketStat[] }) {
  return (
    <section style={detailSection}>
      <h3 style={detailHeading}>{title}</h3>
      <div style={tableGrid}>
        <span>Type</span>
        <span>Hits</span>
        <span>Rate</span>
        <span>Avg</span>
        {rows.map((row) => (
          <React.Fragment key={`${title}-${row.label}`}>
            <strong>{row.label}</strong>
            <span>{`${row.hits}/${row.total}`}</span>
            <span>{`${Math.round(row.hitRate * 100)}%`}</span>
            <span>{row.averagePoints}</span>
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}

function ResultDetailsPanel({
  stats,
  onClose,
}: {
  stats: RunPerformanceStats;
  onClose: () => void;
}) {
  return (
    <div style={detailsPanel}>
      <div style={detailsHeader}>
        <div>
          <div style={eyebrow}>PERFORMANCE</div>
          <h2 style={detailsTitle}>{currentRunTitle()}</h2>
        </div>
        <button onClick={onClose} style={secondaryAction}>
          Close
        </button>
      </div>
      <div style={summaryGrid}>
        <div>
          <span>Hit rate</span>
          <strong>{stats.accuracyLabel}</strong>
        </div>
        <div>
          <span>Targets</span>
          <strong>{`${stats.hitTargets}/${stats.totalTargets}`}</strong>
        </div>
        <div>
          <span>Missed</span>
          <strong>{stats.missedTargets}</strong>
        </div>
        <div>
          <span>Wrong dir</span>
          <strong>{stats.wrongDirectionTargets}</strong>
        </div>
        <div>
          <span>Tenouchi</span>
          <strong>{stats.tenouchiHits}</strong>
        </div>
        <div>
          <span>Obstacles</span>
          <strong>{`${stats.obstacleCleared}/${stats.obstacleTotal}`}</strong>
        </div>
      </div>
      <div style={detailsColumns}>
        <StatsTable title="By direction" rows={stats.byDirection} />
        <StatsTable title="By hand" rows={stats.byHand} />
        <StatsTable title="By size" rows={stats.bySize} />
        <StatsTable title="By speed" rows={stats.bySpeed} />
      </div>
      <div style={detailsColumns}>
        <section style={detailSection}>
          <h3 style={detailHeading}>Best cuts</h3>
          {stats.bestCuts.length === 0 ? (
            <p style={mutedText}>No scored cuts.</p>
          ) : (
            stats.bestCuts.map((cut) => (
              <div key={`best-${cut.id}`} style={listRow}>
                <span>{`${cut.dir} ${gradeEnglish[cut.grade]}`}</span>
                <strong>{`${cut.points} pts`}</strong>
              </div>
            ))
          )}
        </section>
        <section style={detailSection}>
          <h3 style={detailHeading}>Weakest cuts</h3>
          {stats.weakestCuts.length === 0 ? (
            <p style={mutedText}>No scored cuts.</p>
          ) : (
            stats.weakestCuts.map((cut) => (
              <div key={`weak-${cut.id}`} style={listRow}>
                <span>{`${cut.dir} ${gradeEnglish[cut.grade]}`}</span>
                <strong>{`${cut.points} pts`}</strong>
              </div>
            ))
          )}
        </section>
        <section style={detailSection}>
          <h3 style={detailHeading}>Recent mistakes</h3>
          {stats.recentMistakes.length === 0 ? (
            <p style={mutedText}>No mistakes recorded.</p>
          ) : (
            stats.recentMistakes.map((event, index) => (
              <div key={`mistake-${index}`} style={listRow}>
                <EventLabel event={event} />
              </div>
            ))
          )}
        </section>
      </div>
    </div>
  );
}

// This overlay is DOM-only and normally never visible inside an active
// immersive session. If the runtime has already ended the session, actions
// that continue gameplay bring VR back before changing the game phase.
function resumeImmersiveIfNeeded() {
  if (!xrStore.getState().session) {
    void xrStore.enterVR().catch(() => undefined);
  }
}

export function ResultOverlay() {
  const phase = useGameStore((s) => s.phase);
  const mode = useGameStore((s) => s.mode);
  const lastRunPassed = useGameStore((s) => s.lastRunPassed);
  const lastRunPhaseIndex = useGameStore((s) => s.lastRunPhaseIndex);
  const resultDetailsOpen = useGameStore((s) => s.resultDetailsOpen);
  const retryCampaignPhase = useGameStore((s) => s.retryCampaignPhase);
  const advanceCampaignPhase = useGameStore((s) => s.advanceCampaignPhase);
  const goToStory = useGameStore((s) => s.goToStory);
  const retrySurvival = useGameStore((s) => s.retrySurvival);
  const resetGame = useGameStore((s) => s.resetGame);
  const setResultDetailsOpen = useGameStore((s) => s.setResultDetailsOpen);
  const stats = useRunStats();

  if (phase !== "win" && phase !== "fail") return null;

  const passed = lastRunPassed === true;
  const canAdvance = passed && lastRunPhaseIndex < CAMPAIGN_PHASES.length - 1;
  const hasStory = passed && CAMPAIGN_PHASES[lastRunPhaseIndex]?.story != null;
  // Only the story/advance paths resume gameplay and need VR back; the
  // terminal "Main Menu" case (resetGame) intentionally stays flat — the
  // Menu screen has its own explicit Enter VR button.
  const continueAction = hasStory
    ? () => {
        resumeImmersiveIfNeeded();
        goToStory();
      }
    : canAdvance
      ? () => {
          resumeImmersiveIfNeeded();
          advanceCampaignPhase();
        }
      : resetGame;
  const handleRetryCampaign = () => {
    resumeImmersiveIfNeeded();
    retryCampaignPhase();
  };
  const handleRetrySurvival = () => {
    resumeImmersiveIfNeeded();
    retrySurvival();
  };
  return (
    <>
      {resultDetailsOpen && (
        <ResultDetailsPanel
          stats={stats}
          onClose={() => setResultDetailsOpen(false)}
        />
      )}
      <div style={overlayActions}>
        <button
          onClick={() => setResultDetailsOpen(!resultDetailsOpen)}
          style={secondaryAction}
        >
          Performance
        </button>
        {mode === "survival" ? (
          <>
            <button onClick={resetGame} style={secondaryAction}>
              Menu
            </button>
            <button onClick={handleRetrySurvival} style={primaryAction}>
              Again
            </button>
          </>
        ) : (
          <>
            <button onClick={handleRetryCampaign} style={secondaryAction}>
              Retry
            </button>
            {passed ? (
              <button onClick={continueAction} style={primaryAction}>
                {canAdvance ? "Next" : "Main Menu"}
              </button>
            ) : (
              <button onClick={resetGame} style={primaryAction}>
                Main Menu
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
}

const overlayActions: React.CSSProperties = {
  position: "fixed",
  bottom: 40,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  gap: 12,
  flexWrap: "wrap",
  justifyContent: "center",
  maxWidth: "min(92vw, 760px)",
};

const primaryAction: React.CSSProperties = {
  ...primaryButton,
  ...typography.ui,
  padding: "14px 28px",
  fontSize: 15,
};

const secondaryAction: React.CSSProperties = {
  ...secondaryButton,
  ...typography.ui,
  padding: "14px 24px",
  fontSize: 15,
};

const detailsPanel: React.CSSProperties = {
  position: "fixed",
  top: 28,
  left: "50%",
  transform: "translateX(-50%)",
  width: "min(980px, calc(100vw - 32px))",
  maxHeight: "calc(100vh - 140px)",
  overflow: "auto",
  padding: 24,
  background: "rgba(20, 16, 14, 0.97)",
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  color: colors.washi,
  ...typography.ui,
  boxShadow: "0 28px 80px rgba(0, 0, 0, 0.55)",
};

const detailsHeader: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 16,
  marginBottom: 18,
};

const eyebrow: React.CSSProperties = {
  color: colors.amber,
  fontSize: 12,
  fontWeight: 900,
};

const detailsTitle: React.CSSProperties = {
  margin: "4px 0 0",
  fontSize: 24,
};

const summaryGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
  gap: 10,
  marginBottom: 18,
};

const detailsColumns: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 12,
  marginBottom: 12,
};

const detailSection: React.CSSProperties = {
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  padding: 14,
  background: "rgba(42, 31, 23, 0.5)",
};

const detailHeading: React.CSSProperties = {
  margin: "0 0 10px",
  color: colors.amber,
  fontSize: 15,
};

const tableGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1.4fr 0.8fr 0.8fr 0.8fr",
  gap: "7px 10px",
  fontSize: 13,
};

const listRow: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  padding: "6px 0",
  borderBottom: `1px solid rgba(107, 78, 50, 0.35)`,
  fontSize: 13,
};

const mutedText: React.CSSProperties = {
  margin: 0,
  color: colors.border,
  fontSize: 13,
};
